/**
 * The control centre for annual reports and wealth declarations — a matrix board with a
 * completion meter instead of dot notation.
 *
 * One component serves both case kinds because they are the same shape. What differs is
 * the trigger and the label, and neither justifies a second screen.
 *
 * The exception panel comes first deliberately: "which cases are blocked on the client"
 * is the question this screen exists to answer, and burying it under an alphabetical
 * list makes the user do the filtering.
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "@onecpa/shared";
import {
  Button,
  type Column,
  CompletionMeter,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  IdentityCell,
  KpiStrip,
  PageHeader,
  Select,
  StatusBadge,
  TabStrip,
} from "@onecpa/ui";
import { api, type CaseRow } from "../api/client.js";
import { useSession } from "../app/session.js";

type Filter = "all" | "not_started" | "in_progress" | "requires_completion" | "ready";

export function CaseList({ kind }: { kind: "annual" | "wealth" }) {
  const session = useSession();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [opening, setOpening] = useState(false);

  const title = kind === "annual" ? "דוחות שנתיים" : "הצהרות הון";

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["cases", kind, session.orgId],
    queryFn: () => api.cases.list(session.orgId, kind),
  });

  const rows = (data?.cases ?? []).filter((row) => filter === "all" || row.status === filter);
  // Left undefined while loading so counts render as loading, not as zero.
  const summary = data?.summary;

  const columns: Column<CaseRow>[] = [
    {
      key: "client",
      header: "לקוח",
      sticky: true,
      width: "230px",
      cell: (row) => (
        <IdentityCell
          name={row.clientName}
          detail={row.clientType === "company" ? "חברה בע״מ" : "עוסק מורשה"}
        />
      ),
    },
    {
      key: "period",
      header: kind === "annual" ? "שנת מס" : "תאריך ההצהרה",
      cell: (row) => (
        <bdi className="tnum text-xs text-ink">
          {kind === "annual"
            ? row.taxYear
            : row.declarationDate
              ? formatDate(row.declarationDate)
              : "—"}
        </bdi>
      ),
    },
    {
      key: "progress",
      header: "השלמה",
      cell: (row) => (
        <CompletionMeter
          closed={row.itemsClosed}
          total={row.itemsTotal}
          blocking={row.itemsBlocking}
        />
      ),
    },
    {
      key: "staff",
      header: "מטפל",
      hideOnSmall: true,
      cell: (row) => (
        <span className="text-xs text-ink-muted">{row.assignedStaffName ?? "לא מוקצה"}</span>
      ),
    },
    {
      key: "due",
      header: "מועד",
      hideOnSmall: true,
      cell: (row) =>
        row.dueDate ? (
          <bdi className="tnum text-xs text-ink-muted">{formatDate(row.dueDate)}</bdi>
        ) : (
          "—"
        ),
    },
    {
      key: "status",
      header: "מצב",
      align: "end",
      cell: (row) => <StatusBadge vocabulary="workspace" status={row.status} />,
    },
  ];

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={data ? `${data.cases.length} תיקים` : undefined}
        action={
          <Button variant="primary" icon="plus" onClick={() => setOpening(true)}>
            פתיחת תיק
          </Button>
        }
      >
        <TabStrip
          tabs={[
            { id: "all", label: "הכל", count: summary?.total },
            {
              id: "requires_completion",
              label: "ממתין ללקוח",
              count: summary?.requires_completion,
            },
            { id: "in_progress", label: "בטיפול", count: summary?.in_progress },
            { id: "ready", label: "מוכן לעבודה", count: summary?.ready },
            { id: "not_started", label: "לא התחיל", count: summary?.not_started },
          ]}
          active={filter}
          onSelect={(id) => setFilter(id as Filter)}
        />
      </PageHeader>

      <KpiStrip
        items={[
          { label: "סה״כ תיקים", value: summary?.total },
          { label: "לא התחילו", value: summary?.not_started, tone: "pending" },
          { label: "בטיפול", value: summary?.in_progress },
          { label: "ממתינים ללקוח", value: summary?.requires_completion, tone: "ochre" },
          { label: "מוכנים לעבודה", value: summary?.ready, tone: "ledger" },
        ]}
      />

      <div className="p-5">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          onRowClick={(row) =>
            void navigate({
              to: kind === "annual" ? "/annual/$caseId" : "/wealth/$caseId",
              params: { caseId: row.id },
            })
          }
          loading={isLoading}
          caption={`${title} לפי לקוח`}
          footnote="מד ההשלמה מציג פריטים שנסגרו מתוך כלל הפריטים · חרדל = קיים פריט שממתין להשלמה מהלקוח"
          empty={
            <EmptyState
              title="אין תיקים פתוחים"
              description="פתיחת תיק יוצרת רשימת מסמכים מהתבניות של המשרד ושולחת בקשה ללקוח."
              action={
                <Button variant="primary" onClick={() => setOpening(true)}>
                  פתיחת תיק
                </Button>
              }
            />
          }
        />
      </div>

      <OpenCaseDialog kind={kind} open={opening} onClose={() => setOpening(false)} />
    </>
  );
}

function OpenCaseDialog({
  kind,
  open,
  onClose,
}: {
  kind: "annual" | "wealth";
  open: boolean;
  onClose(): void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [year, setYear] = useState(String(new Date().getUTCFullYear() - 1));
  const [error, setError] = useState<string | null>(null);

  const { data: clients } = useQuery({
    queryKey: ["clients", session.orgId, "", "active"],
    queryFn: () => api.clients.list(session.orgId, { status: "active", limit: 200 }),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      api.cases.open(session.orgId, kind, {
        clientId,
        ...(kind === "annual" ? { taxYear: Number(year) } : { declarationDate: `${year}-12-31` }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
      session.toast("התיק נפתח ונשלחה בקשה ללקוח");
      onClose();
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={kind === "annual" ? "פתיחת דוח שנתי" : "פתיחת הצהרת הון"}
      description="רשימת המסמכים תיווצר מהתבניות של המשרד, לפי סוג הלקוח."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button
            variant="primary"
            disabled={!clientId}
            loading={create.isPending}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
          >
            פתיחה
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="לקוח" required>
          {({ id }) => (
            <Select id={id} value={clientId} onChange={(event) => setClientId(event.target.value)}>
              <option value="">בחרו לקוח</option>
              {(clients?.clients ?? []).map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={kind === "annual" ? "שנת מס" : "שנת ההצהרה"} required>
          {({ id }) => (
            <Select id={id} value={year} onChange={(event) => setYear(event.target.value)}>
              {[0, 1, 2, 3].map((offset) => {
                const value = new Date().getUTCFullYear() - offset;
                return (
                  <option key={value} value={value}>
                    {value}
                  </option>
                );
              })}
            </Select>
          )}
        </Field>

        {error && (
          <p role="alert" className="text-xs font-semibold text-red-ink">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
